import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MaskedLines, Reveal } from "@/components/motion";

const SECTIONS = [
  {
    title: "What we collect",
    body: "When you submit an enquiry through this website, we collect your name, email address, the service you're interested in, and the contents of your message. If you book a call, download a guide, or join the email list via our third-party tools (Calendly and Flodesk), those platforms collect information under their own privacy policies.",
  },
  {
    title: "How we use it",
    body: "Your information is used for one purpose only: to respond to your enquiry and, where relevant, to begin a coaching conversation with you. We do not sell, rent, or share your personal information with third parties for marketing purposes.",
  },
  {
    title: "Storage & security",
    body: "Enquiry details are stored securely and are accessible only to The Strength Movement. We keep enquiries only as long as needed to manage our relationship with you, after which they may be deleted.",
  },
  {
    title: "Third-party services",
    body: "This site links to and embeds third-party services, Calendly (booking), Flodesk (guides and email list), Spotify (podcast), and Instagram. When you use those services, their own privacy policies apply, and we encourage you to read them.",
  },
  {
    title: "Your rights",
    body: "You may request a copy of the personal information we hold about you, ask for it to be corrected, or ask for it to be deleted at any time. To make a request, simply get in touch via the contact form.",
  },
  {
    title: "Changes to this policy",
    body: "This policy may be updated from time to time as the business evolves. The current version will always be published on this page.",
  },
];

const Privacy = () => {

  return (
    <main data-testid="privacy-page">
      <section className="bg-mist grain">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-32 md:pt-44 pb-16 md:pb-20">
          <p className="label-text text-ocean" data-testid="privacy-eyebrow">The fine print</p>
          <h1
            className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tight"
            data-testid="privacy-heading"
          >
            <MaskedLines lines={["Privacy Policy"]} />
          </h1>
          <p className="mt-6 text-sm text-ink/50" data-testid="privacy-updated">
            Last updated: July 2026
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 py-20 md:py-28">
          <Reveal>
            <p className="text-base md:text-lg leading-relaxed text-ink/70">
              The Strength Movement respects your privacy. This policy explains,
              in plain language, what information is collected through this
              website and how it's used.
            </p>
          </Reveal>
          <div className="mt-14 space-y-12">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={0.03 * i}>
                <section data-testid={`privacy-section-${i}`}>
                  <h2 className="flex items-baseline gap-4 font-display text-2xl md:text-3xl">
                    <span className="text-lg italic text-ocean/70">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {s.title}
                  </h2>
                  <p className="mt-4 text-sm md:text-base leading-relaxed text-ink/65">{s.body}</p>
                </section>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-16 border-t border-ink/10 pt-8 text-sm text-ink/60">
              Questions about this policy?{" "}
              <Link
                to="/contact"
                data-testid="privacy-contact-link"
                className="font-bold text-ocean underline-offset-4 hover:underline"
              >
                Get in touch
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Privacy;
